import {
  Visibility as EyeIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Build as ToolIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  aiManagerApi,
  Company,
  Statistics,
  Task,
  Tool
} from '../../apis/ai-manager';
import TaskDetailModal from './components/TaskDetailModal';
import ToolDetailModal from './components/ToolDetailModal';
import ToolTestModal from './components/ToolTestModal';

const AiManagerDashboard: React.FC = () => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [statistics, setStatistics] = useState<Statistics>({
    totalTools: 0,
    activeTools: 0,
    totalTasks: 0,
    activeTasks: 0,
    todayExecutions: 0,
    avgSuccessRate: 0
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>(''); // '' for all companies
  const [loading, setLoading] = useState(true);
  const [toolTestModalVisible, setToolTestModalVisible] = useState(false);
  const [taskDetailModalVisible, setTaskDetailModalVisible] = useState(false);
  const [toolDetailModalVisible, setToolDetailModalVisible] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [toolPage, setToolPage] = useState(0);
  const [taskPage, setTaskPage] = useState(0);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error'}>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [selectedCompanyId]);

  const showMessage = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadCompanies = async () => {
    try {
      console.log('🔄 Loading companies...');
      const companies = await aiManagerApi.getCompanies();
      console.log('✅ Companies loaded:', companies);
      setCompanies(companies);
    } catch (error) {
      console.error('❌ Failed to load companies:', error);
      showMessage('도장 목록 조회에 실패했습니다.', 'error');
    }
  };

  const handleCompanyChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedCompanyId(event.target.value as number | '');
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // 선택된 도장이 있으면 쿼리 파라미터로 추가
      const comId = selectedCompanyId ? selectedCompanyId as number : undefined;
      
      const dashboardData = await aiManagerApi.getDashboardData(comId);
      
      setTools(dashboardData.tools);
      setTasks(dashboardData.tasks);
      setStatistics(dashboardData.statistics);

    } catch (error) {
      console.error('Dashboard data loading failed:', error);
      showMessage('데이터 로딩에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToolStatusChange = async (toolId: number, isActive: boolean) => {
    try {
      const success = await aiManagerApi.updateToolStatus(toolId, isActive);

      if (success) {
        setTools(prev => prev.map(tool => 
          tool.toolId === toolId ? { ...tool, isActive } : tool
        ));
        showMessage(`Tool이 ${isActive ? '활성화' : '비활성화'}되었습니다.`, 'success');
      } else {
        showMessage('상태 변경에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Tool status change failed:', error);
      showMessage('상태 변경에 실패했습니다.', 'error');
    }
  };

  const handleTaskStatusChange = async (taskId: number, isActive: boolean) => {
    try {
      const success = await aiManagerApi.updateTaskStatus(taskId, isActive);

      if (success) {
        setTasks(prev => prev.map(task => 
          task.taskId === taskId ? { ...task, isActive } : task
        ));
        showMessage(`Task가 ${isActive ? '활성화' : '비활성화'}되었습니다.`, 'success');
      } else {
        showMessage('상태 변경에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Task status change failed:', error);
      showMessage('상태 변경에 실패했습니다.', 'error');
    }
  };

  const handleToolCompanyUsageToggle = async (toolId: number, comId: number, isEnabled: boolean) => {
    try {
      const success = await aiManagerApi.updateToolCompanyUsage(toolId, comId, isEnabled);

      if (success) {
        showMessage(`도장 ${comId}에서 Tool ${toolId}이 ${isEnabled ? '활성화' : '비활성화'}되었습니다.`, 'success');
        // 선택된 도장이 있으면 데이터 새로고침
        if (selectedCompanyId === comId) {
          loadDashboardData();
        }
      } else {
        showMessage('도장별 Tool 사용여부 변경에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Tool company usage toggle failed:', error);
      showMessage('도장별 Tool 사용여부 변경에 실패했습니다.', 'error');
    }
  };

  const handleTaskCompanyUsageToggle = async (taskId: number, comId: number, isEnabled: boolean) => {
    try {
      const success = await aiManagerApi.updateTaskCompanyUsage(taskId, comId, isEnabled);

      if (success) {
        showMessage(`도장 ${comId}에서 Task ${taskId}가 ${isEnabled ? '활성화' : '비활성화'}되었습니다.`, 'success');
        // 선택된 도장이 있으면 데이터 새로고침
        if (selectedCompanyId === comId) {
          loadDashboardData();
        }
      } else {
        showMessage('도장별 Task 사용여부 변경에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Task company usage toggle failed:', error);
      showMessage('도장별 Task 사용여부 변경에 실패했습니다.', 'error');
    }
  };

  const handleToolTest = (tool: Tool) => {
    setSelectedTool(tool);
    setToolTestModalVisible(true);
  };

  const handleTaskDetail = (task: Task) => {
    setSelectedTask(task);
    setTaskDetailModalVisible(true);
  };

  const handleToolDetail = (tool: Tool) => {
    setSelectedTool(tool);
    setToolDetailModalVisible(true);
  };

  const getCategoryColor = (category: string): "primary" | "secondary" | "success" | "error" | "info" | "warning" => {
    const colors: Record<string, "primary" | "secondary" | "success" | "error" | "info" | "warning"> = {
      email_management: 'primary',
      user_management: 'success',
      system_monitoring: 'warning',
      content_management: 'secondary',
      analytics: 'info',
      utility: 'secondary'
    };
    return colors[category] || 'secondary';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* 헤더와 도장 선택 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          AI Manager 관리
        </Typography>
        
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="company-select-label">도장 선택</InputLabel>
          <Select
            labelId="company-select-label"
            value={selectedCompanyId}
            label="도장 선택"
            onChange={handleCompanyChange}
          >
            <MenuItem value="">
              <em>전체 도장</em>
            </MenuItem>
            {companies.length > 0 ? (
              companies.map((company) => (
                <MenuItem key={company.comId} value={company.comId}>
                  {company.comNm}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <em>도장 정보를 불러오는 중...</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>

      {/* 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ToolIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{statistics.totalTools}</Typography>
                  <Typography variant="body2" color="text.secondary">전체 Tools</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PlayIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6" color="success.main">{statistics.activeTools}</Typography>
                  <Typography variant="body2" color="text.secondary">활성 Tools</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SettingsIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{statistics.totalTasks}</Typography>
                  <Typography variant="body2" color="text.secondary">전체 Tasks</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PlayIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6" color="success.main">{statistics.activeTasks}</Typography>
                  <Typography variant="body2" color="text.secondary">활성 Tasks</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{statistics.todayExecutions}</Typography>
                  <Typography variant="body2" color="text.secondary">오늘 실행</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{statistics.avgSuccessRate}%</Typography>
                  <Typography variant="body2" color="text.secondary">평균 성공률</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tasks 테이블 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">AI Tasks 관리</Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadDashboardData}
              disabled={loading}
            >
              새로고침
            </Button>
          </Box>
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Task 이름</TableCell>
                    <TableCell>사용 도구</TableCell>
                    <TableCell>전역 상태</TableCell>
                    {selectedCompanyId && <TableCell>도장별 사용여부</TableCell>}
                    <TableCell>통계</TableCell>
                    <TableCell>액션</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.slice(taskPage * 10, (taskPage + 1) * 10).map((task) => (
                    <TableRow key={task.taskId}>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {task.taskName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {task.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {task.tools.map(tool => (
                            <Chip 
                              key={tool} 
                              label={tool} 
                              color="success" 
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={task.isActive}
                              onChange={(e) => handleTaskStatusChange(task.taskId, e.target.checked)}
                            />
                          }
                          label={task.isActive ? '활성' : '비활성'}
                        />
                      </TableCell>
                      {selectedCompanyId && (
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={task.isActive} // TODO: 실제 도장별 사용여부 상태로 변경
                                onChange={(e) => handleTaskCompanyUsageToggle(
                                  task.taskId, 
                                  selectedCompanyId as number, 
                                  e.target.checked
                                )}
                              />
                            }
                            label={task.isActive ? '사용' : '미사용'} // TODO: 실제 도장별 사용여부 상태로 변경
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box>
                          <Typography variant="body2">실행: {task.executionCount}회</Typography>
                          <Typography variant="body2">성공률: {task.successRate}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EyeIcon />}
                          onClick={() => handleTaskDetail(task)}
                        >
                          상세보기
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={tasks.length}
                page={taskPage}
                onPageChange={(_, newPage) => setTaskPage(newPage)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Tools 테이블 */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>AI Tools 관리</Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tool 이름</TableCell>
                  <TableCell>카테고리</TableCell>
                  <TableCell>전역 상태</TableCell>
                  {selectedCompanyId && <TableCell>도장별 사용여부</TableCell>}
                  <TableCell>성능 지표</TableCell>
                  <TableCell>액션</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tools.slice(toolPage * 10, (toolPage + 1) * 10).map((tool) => (
                  <TableRow key={tool.toolId}>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {tool.toolName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tool.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={tool.category} 
                        color={getCategoryColor(tool.category)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={tool.isActive}
                            onChange={(e) => handleToolStatusChange(tool.toolId, e.target.checked)}
                          />
                        }
                        label={tool.isActive ? '활성' : '비활성'}
                      />
                    </TableCell>
                    {selectedCompanyId && (
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={tool.isActive} // TODO: 실제 도장별 사용여부 상태로 변경
                              onChange={(e) => handleToolCompanyUsageToggle(
                                tool.toolId, 
                                selectedCompanyId as number, 
                                e.target.checked
                              )}
                            />
                          }
                          label={tool.isActive ? '사용' : '미사용'} // TODO: 실제 도장별 사용여부 상태로 변경
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box>
                        <Typography variant="body2">실행: {tool.executionCount}회</Typography>
                        <Typography variant="body2">성공률: {tool.successRate}%</Typography>
                        <Typography variant="body2">평균: {tool.avgExecutionTime}ms</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EyeIcon />}
                          onClick={() => handleToolDetail(tool)}
                        >
                          상세보기
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PlayIcon />}
                          onClick={() => handleToolTest(tool)}
                        >
                          테스트
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={tools.length}
              page={toolPage}
              onPageChange={(_, newPage) => setToolPage(newPage)}
              rowsPerPage={10}
              rowsPerPageOptions={[10]}
            />
          </TableContainer>
        </CardContent>
      </Card>

      {/* Tool 테스트 모달 */}
      <ToolTestModal
        open={toolTestModalVisible}
        tool={selectedTool}
        onClose={() => setToolTestModalVisible(false)}
      />

      {/* Task 상세보기 모달 */}
      <TaskDetailModal
        open={taskDetailModalVisible}
        task={selectedTask}
        onClose={() => setTaskDetailModalVisible(false)}
      />

      {/* Tool 상세보기 모달 */}
      <ToolDetailModal
        open={toolDetailModalVisible}
        tool={selectedTool}
        onClose={() => setToolDetailModalVisible(false)}
      />

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AiManagerDashboard;
